// ======================================
// Authentication Utilities
// ======================================

import { authAPI } from './api.js';

const TOKEN_KEY = 'nvi_token';
const USER_KEY = 'nvi_user';

/**
 * Get the current auth token
 * Checks for expiry and clears if expired
 */
export function getToken() {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return null;

    try {
        // Validation: Check if token is expired
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));

        const payload = JSON.parse(jsonPayload);
        const currentTime = Math.floor(Date.now() / 1000);

        if (payload.exp < currentTime) {
            // Token expired - clear it and return null
            localStorage.removeItem(TOKEN_KEY);
            localStorage.removeItem(USER_KEY);
            return null;
        }
    } catch (e) {
        // Invalid token format
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        return null;
    }

    return token;
}

/**
 * Get the current user
 */
export function getUser() {
    const userStr = localStorage.getItem(USER_KEY);
    if (!userStr) return null;
    try {
        return JSON.parse(userStr);
    } catch {
        return null;
    }
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated() {
    return !!getToken();
}

/**
 * Check if user has a specific role
 */
export function hasRole(roles) {
    const user = getUser();
    if (!user) return false;
    if (typeof roles === 'string') {
        return user.role === roles;
    }
    return roles.includes(user.role);
}

/**
 * Check if user is admin
 */
export function isAdmin() {
    return hasRole(['admin', 'manager']);
}

/**
 * Check if user is staff (admin, manager, or designer)
 */
export function isStaff() {
    return hasRole(['admin', 'manager', 'designer']);
}

/**
 * Login user
 * @param {string} email 
 * @param {string} password 
 * @returns {Promise<object>} User data
 */
export async function login(email, password) {
    try {
        const response = await authAPI.login(email, password);

        if (response.success && response.data) {
            localStorage.setItem(TOKEN_KEY, response.data.token);
            localStorage.setItem(USER_KEY, JSON.stringify(response.data.user));
            return response.data.user;
        }

        throw new Error(response.error || 'Login failed');
    } catch (error) {
        // Demo mode: if backend is unreachable (local dev without PHP), allow demo login
        if (window.location.hostname === 'localhost' &&
            (error.message === 'Failed to fetch' || error.message.includes('NetworkError'))) {

            const demoUsers = {
                'admin@nammaveetuinteriors.com': { id: 1, name: 'Admin User', email: 'admin@nammaveetuinteriors.com', role: 'admin', phone: '+919876543210' },
                'designer@nvi.com': { id: 2, name: 'Designer', email: 'designer@nvi.com', role: 'designer', phone: '+919876543211' }
            };

            const demoUser = demoUsers[email];
            if (demoUser && password.length >= 5) {
                const mockToken = 'demo_token_' + Date.now();
                localStorage.setItem(TOKEN_KEY, mockToken);
                localStorage.setItem(USER_KEY, JSON.stringify(demoUser));
                console.warn('⚠️ DEMO MODE: Logged in without backend. Data will be simulated.');
                return demoUser;
            }
            throw new Error('Demo mode: Use admin@nammaveetuinteriors.com with any password (5+ chars)');
        }
        throw error;
    }
}

/**
 * Register new user
 * @param {object} data - User registration data
 * @returns {Promise<object>} User data
 */
export async function register(data) {
    const response = await authAPI.register(data);

    if (response.success && response.data) {
        localStorage.setItem(TOKEN_KEY, response.data.token);
        localStorage.setItem(USER_KEY, JSON.stringify(response.data.user));
        return response.data.user;
    }

    throw new Error(response.error || 'Registration failed');
}

/**
 * Logout user
 */
export function logout() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    // Use relative path to redirect to home within the current subdirectory
    window.location.href = './';
}

/**
 * Refresh user data from server
 */
export async function refreshUser() {
    try {
        const response = await authAPI.me();
        if (response.success && response.data) {
            localStorage.setItem(USER_KEY, JSON.stringify(response.data));
            return response.data;
        }
    } catch (error) {
        console.error('Failed to refresh user:', error);
        logout();
    }
    return null;
}

/**
 * Require authentication - redirect to login if not authenticated
 * @param {string[]} requiredRoles - Optional required roles
 * @returns {boolean} Whether access is allowed
 */
export function requireAuth(requiredRoles = []) {
    if (!isAuthenticated()) {
        window.location.href = '/login';
        return false;
    }

    if (requiredRoles.length > 0 && !hasRole(requiredRoles)) {
        window.location.href = '/';
        return false;
    }

    return true;
}

/**
 * Get auth header for API requests
 */
export function getAuthHeader() {
    const token = getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
}
